<template>
	<AppDropdown
		:popover-props="{
			distance: 0,
			skidding: -8,
			placement: 'bottom-start',
		}"
		:trigger-button-props="{
			'v-tooltip': triggerButtonLabel,
			'aria-label': triggerButtonLabel,
			class: [
				'activity-type-inline-select-button',
				{
					'activity-type-inline-select-button--full-width':
						!activityType,
					'activity-type-inline-select-button--open': isOpen,
				},
			],
		}"
		@show="isOpen = true"
		@hide="isOpen = false"
		@apply-show="focusInput"
	>
		<template #trigger>
			<SuiRelatedContent
				v-if="activityType"
				icon="icon-bolt-16"
				:type="isDone ? 'secondary' : 'primary'"
			>
				<div
					ref="$textRef"
					v-tooltip="isOverflowing ? activityType?.name : undefined"
					class="activity-type-inline-select-button__text"
				>
					{{ activityType?.name }}
				</div>
			</SuiRelatedContent>
		</template>

		<template #default="{ closeDropdown }">
			<ActivityTypeInlineSelect
				ref="$dropdownRef"
				:activity-type="activityType"
				@close="closeDropdown"
				@select="onActivityTypeSelect"
			/>
		</template>
	</AppDropdown>
</template>

<script setup lang="ts">
import { SuiRelatedContent } from '@developers/scoro-ui';
import { AppDropdown } from '@/features/App';
import { defineAsyncComponent, ref, computed } from 'vue';
import { useTranslation } from '@/composables/useTranslation';
import { wait } from '@/utils/misc';
import { Timing } from '@/constants/timing';
import type { ActivityTypeInlineSelectFragment } from '@/generated/graphql';
import { useIsElementOverflowing } from '@/composables';

const ActivityTypeInlineSelect = defineAsyncComponent(
	() =>
		import('@/features/ActivityType/components/ActivityTypeInlineSelect/ActivityTypeInlineSelect.vue')
);

export interface ActivityTypeInlineSelectButtonProps {
	activityType?: ActivityTypeInlineSelectFragment | null;
	isDone?: boolean;
}

const { activityType = null } =
	defineProps<ActivityTypeInlineSelectButtonProps>();

const emit = defineEmits<{
	(e: 'select', activityType: ActivityTypeInlineSelectFragment): void;
}>();

const isOpen = ref(false);
const $textRef = ref<HTMLDivElement | null>(null);
const $dropdownRef = ref<typeof ActivityTypeInlineSelect | null>(null);

const { t } = useTranslation();
const isOverflowing = useIsElementOverflowing<HTMLSpanElement>($textRef);

const triggerButtonLabel = computed(() =>
	activityType ? undefined : t('select_activity_type')
);

const focusInput = async () => {
	await wait(Timing.DROPDOWN_FOCUS_DELAY_MS);
	$dropdownRef.value?.$refs?.$searchInput?.$refs?.$input?.input?.focus();
};

const onActivityTypeSelect = (
	activityType: ActivityTypeInlineSelectFragment
) => {
	emit('select', activityType);
};
</script>

<style lang="scss">
.activity-type-inline-select-button {
	min-width: 14rem;
	min-height: 4rem;
	border-radius: 0.5rem;
	padding: 1rem;
	margin-left: -1rem;
	text-align: left;

	&:hover,
	&--open {
		background-color: $neutral-20;
	}

	&--full-width {
		width: 100%;
	}

	.sui-related-content__content {
		overflow: hidden;
	}

	&__text {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}
</style>
